import { LocationPage } from "@/components/pages/LocationPageTh";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import {
  Phone,
  Mail,
  MessageCircle,
  MapPin,
  ArrowLeft,
  CheckCircle2,
  Navigation,
  ArrowRight,
} from "lucide-react";
import { getLocation, locations } from "@/data/locations";
import { LocationDeepLinks } from "@/components/LocationDeepLinks";
import { EntityPanel } from "@/components/EntityPanel";
import { consolidateGraph } from "@/lib/schema-graph";
import { programmaticRating } from "@/lib/programmatic-ratings";
import { BUILD_DATE, speakableSchema, faqPageSchema } from "@/lib/aeo";

const SITE = "https://nyclanguageinstitute.com";
const ORG_NAME = "NYC Legal & Notary Service Co., Ltd.";
const ORG_PHONE = "+66-83-249-4999";
const HQ_ADDRESS = {
  "@type": "PostalAddress" as const,
  streetAddress: "61 Soi Lat Phrao 95 (Prang Thip), Khlong Chaokhun Sing, Wang Thonglang",
  addressLocality: "Bangkok",
  postalCode: "10310",
  addressCountry: "TH",
};

// Strip undefined/null/empty values recursively (keeps JSON-LD clean)
function clean<T>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(clean).filter((v) => v !== undefined) as unknown as T;
  if (obj && typeof obj === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      if (v === undefined || v === null || v === "") continue;
      const cleaned = clean(v);
      if (
        cleaned &&
        typeof cleaned === "object" &&
        !Array.isArray(cleaned) &&
        Object.keys(cleaned).length === 0
      )
        continue;
      out[k] = cleaned;
    }
    return out as T;
  }
  return obj;
}

export const Route = createFileRoute("/locations/$slug")({
  loader: ({ params }) => {
    const loc = getLocation(params.slug);
    if (!loc) throw notFound();
    return { loc };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData)
      return {
        meta: [{ title: ctaTitle("Location — NYC Language Institute", "/locations/$slug", "th") }],
      };
    const { loc } = loaderData;
    const TITLE = `${loc.nameTh} — บริการแปลและรับรองเอกสาร | NYC Language Institute`;
    const DESC = `${loc.description ?? `บริการแปลและรับรองเอกสารใน${loc.nameTh}`} · Notary Public · NAATI · รับรองกงสุล MFA · โทร 083-249-4999`;
    const url = `${SITE}/locations/${params.slug}`;

    const isBranch = loc.type === "headquarters" || loc.type === "branch";
    const rating = programmaticRating(`location:${loc.slug}`);
    const mapsUrl =
      loc.mapUrl ??
      (loc.lat && loc.lng
        ? `https://www.google.com/maps/search/?api=1&query=${loc.lat},${loc.lng}`
        : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address ?? loc.nameTh)}`);

    let jsonLd: Record<string, unknown>;

    if (isBranch) {
      jsonLd = clean({
        "@context": "https://schema.org",
        "@type": "LegalService",
        "@id": `${url}#legalservice`,
        name: `${ORG_NAME} — ${loc.nameTh}`,
        alternateName: loc.nameEn,
        url,
        telephone: loc.phone ?? ORG_PHONE,
        email: "contact@nyclanguageinstitute.com",
        description: loc.description,
        priceRange: "฿฿",
        image: `${SITE}/apple-touch-icon.png`,
        currenciesAccepted: "THB, USD, EUR, GBP, JPY, CNY, AUD, SGD",
        paymentAccepted: "Cash, Credit Card, Bank Transfer, PromptPay, QR Code",
        address: loc.address
          ? {
              "@type": "PostalAddress",
              streetAddress: loc.address,
              addressLocality: loc.region === "กรุงเทพมหานคร" ? "Bangkok" : loc.nameEn,
              postalCode: loc.postalCode,
              addressCountry: "TH",
            }
          : undefined,
        geo:
          loc.lat && loc.lng
            ? { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng }
            : undefined,
        hasMap: mapsUrl,
        areaServed: { "@type": "AdministrativeArea", name: loc.nameTh },
        openingHours: "Mo-Sa 09:00-18:00",
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            opens: "09:00",
            closes: "18:00",
          },
        ],
        // aggregateRating removed (GSC Round 10): programmatic ratings violate review-snippet policy.
        parentOrganization: { "@id": `${SITE}#org` },
        isPartOf: { "@id": `${SITE}#website` },
        sameAs: [mapsUrl],
        dateModified: BUILD_DATE,
      });
    } else {
      // Agents (district/province): Service schema with provider pointing to verified org
      jsonLd = clean({
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${url}#service`,
        name: `บริการแปลและรับรองเอกสาร — ${loc.nameTh}`,
        alternateName: `Legal Translation & Notary — ${loc.nameEn}`,
        url,
        serviceType: "Legal Translation & Notarization",
        description: loc.description,
        areaServed: {
          "@type": "AdministrativeArea",
          name: loc.nameTh,
          ...(loc.lat && loc.lng
            ? { geo: { "@type": "GeoCoordinates", latitude: loc.lat, longitude: loc.lng } }
            : {}),
        },
        hasMap: mapsUrl,
        provider: {
          "@type": "LegalService",
          "@id": `${SITE}#org`,
          name: ORG_NAME,
          telephone: ORG_PHONE,
          url: SITE,
          address: HQ_ADDRESS,
        },
        // aggregateRating removed (GSC Round 10): programmatic ratings violate review-snippet policy.
        isPartOf: { "@id": `${SITE}#website` },
        dateModified: BUILD_DATE,
      });
    }

    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE },
        { "@type": "ListItem", position: 2, name: "Locations", item: `${SITE}/locations` },
        { "@type": "ListItem", position: 3, name: loc.nameTh, item: url },
      ],
    };

    return {
      meta: [
        { title: ctaTitle(TITLE, "/locations/$slug", "th") },
        { name: "description", content: ctaDesc(DESC, "/locations/$slug", "th") },
        { property: "og:title", content: ctaTitle(TITLE, "/locations/$slug", "th") },
        { property: "og:description", content: ctaDesc(DESC, "/locations/$slug", "th") },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [
        { rel: "canonical", href: url },
        { rel: "alternate", hrefLang: "th-TH", href: url },
        { rel: "alternate", hrefLang: "x-default", href: url },
        { rel: "alternate", hrefLang: "en", href: url.replace(SITE, `${SITE}/en`) },
      ],
      scripts: [
        speakableSchema(),
        consolidateGraph([
          { type: "application/ld+json", children: JSON.stringify(jsonLd) },
          { type: "application/ld+json", children: JSON.stringify(breadcrumb) },
          faqPageSchema([
            {
              q: `NYC Language Institute ให้บริการในพื้นที่ ${loc.nameTh} หรือไม่?`,
              a: `ใช่ครับ NYC ให้บริการแปลเอกสาร รับรอง Notary Public รับรองกงสุล MFA และบริการ NAATI ครอบคลุมพื้นที่ ${loc.nameTh} (${loc.nameEn}) สามารถนัดรับ-ส่งเอกสารถึงที่ หรือเดินทางมาที่สำนักงานลาดพร้าว 95 ได้`,
            },
            {
              q: `เดินทางมาสำนักงานจาก ${loc.nameTh} ใช้เวลาเท่าไหร่?`,
              a: `สำนักงานใหญ่อยู่ที่ลาดพร้าว 95 ใกล้ MRT ลาดพร้าว สามารถเดินทางจาก ${loc.nameTh} ด้วยรถยนต์ส่วนตัวหรือขนส่งสาธารณะ หากไม่สะดวกเดินทาง NYC มีบริการรับ-ส่งเอกสารถึงที่ภายในเขตกรุงเทพและปริมณฑล`,
            },
            {
              q: `ราคาบริการในพื้นที่ ${loc.nameTh} แพงกว่าที่อื่นไหม?`,
              a: `ไม่ครับ ราคาเดียวกันทุกพื้นที่ทั่วประเทศไทย แปลรับรองเริ่ม ฿500/หน้า, Notary Public เริ่ม ฿1,500/ฉบับ, รับรองกงสุล MFA เริ่ม ฿1,500/ฉบับ ไม่มีค่าใช้จ่ายเพิ่มเติมตามพื้นที่`,
            },
            {
              q: `บริการในพื้นที่ ${loc.nameTh} ใช้เวลานานแค่ไหน?`,
              a: `งานแปลปกติเสร็จภายใน 1-3 วันทำการ, Notary Public รอรับได้ทันที, รับรองกงสุลภายใน 2-3 วัน, NAATI online ภายใน 24 ชั่วโมง สามารถสอบถามคิวงานในพื้นที่ ${loc.nameTh} ได้ที่ LINE @nycli`,
            },
            {
              q: `รับเอกสารที่ ${loc.nameTh} ได้อย่างไร?`,
              a: `เลือกได้ 3 ช่องทาง: (1) มารับด้วยตนเองที่สำนักงานลาดพร้าว 95, (2) จัดส่ง EMS/Kerry/Grab ทั่วประเทศ, (3) ส่ง PDF + ต้นฉบับตามมาทาง EMS สำหรับลูกค้าใน ${loc.nameTh}`,
            },
          ]),
        ]),
      ],
    };
  },
  component: LocationPage,
  notFoundComponent: () => (
    <main className="flex min-h-screen items-center justify-center bg-navy-deep text-white">
      <div className="text-center">
        <h1 className="text-3xl font-bold">ไม่พบพื้นที่ให้บริการนี้</h1>
        <Link to="/locations" className="mt-4 inline-block text-gold underline">
          กลับสู่รายการพื้นที่ทั้งหมด
        </Link>
      </div>
    </main>
  ),
});
